import { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import Raven from 'raven-js';
import { getFullUrl } from 'hubspot-url-utils';
import http from 'hub-http/clients/noAuthApiClient';
import { registerQuery, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { VIDEO_TIMESTAMP_PARAM } from './videoConstants';
// @ts-expect-error cannot find  typed file
import { useSearchParam } from '../../hooks/searchParamHooks';
export const useVideoParams = () => {
  const [[startTime]] = useSearchParam(VIDEO_TIMESTAMP_PARAM);
  return {
    startTime: startTime ? Number(startTime) : undefined
  };
};
export const useAutoClearVideoVTS = () => {
  const {
    startTime
  } = useVideoParams();
  const history = useHistory();
  useEffect(() => () => {
    if (startTime) {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete(VIDEO_TIMESTAMP_PARAM);
      history.replace(Object.assign({}, history.location, {
        search: searchParams.toString()
      }));
    }
  }, [history, startTime]);
};
const videoPlayerConfigDefaultState = {
  autoPlay: false,
  playSpeed: 100
};
export const useVideoPlayerConfig = create()(persist(set => Object.assign({}, videoPlayerConfigDefaultState, {
  toggleAutoPlay() {
    set(state => ({
      autoPlay: !state.autoPlay
    }));
  },
  setPlaySpeed(speed) {
    set({
      playSpeed: speed
    });
  },
  setSubtitleLanguage(subtitleLanguage) {
    set({
      subtitleLanguage
    });
  }
}), {
  name: 'academy:videoPreferences'
}));
export const resetVideoPlayerConfig = () => useVideoPlayerConfig.setState(videoPlayerConfigDefaultState);
export const useVideoTimestamp = create(set => ({
  videoTimestamp: undefined,
  // Workaround: the video player triggers a seek only when the startTime prop
  // changes. Adding a tiny offset (1ms) forces a new prop value so repeated
  // clicks on the same timestamp still trigger a seek. Ideally the player would
  // expose an imperative seekTo(time) API instead of relying on prop changes.
  setVideoTimestamp: timestamp => set(state => ({
    videoTimestamp: state.videoTimestamp === undefined || state.videoTimestamp === timestamp ? timestamp + 0.001 : timestamp
  }))
}));
export const getVideoCaptionData = (videoId, creatorRegion, creatorHubId) => {
  const baseUrl = getFullUrl('api', {
    hubletOverride: creatorRegion || undefined
  });
  return http.get(`${baseUrl}/video/v1/public/${videoId}/subtitles?portalId=${creatorHubId}`, {
    withCredentials: false
  });
};
export const GET_VIDEO_CAPTION_DATA = registerQuery({
  fieldName: 'captionData',
  args: ['videoId', 'creatorRegion', 'creatorHubId'],
  fetcher({
    videoId,
    creatorRegion,
    creatorHubId
  }) {
    if (videoId && creatorRegion && creatorHubId) return getVideoCaptionData(videoId, creatorRegion, creatorHubId);
    return undefined;
  }
});
export const useVideoPlayerCaptions = (videoId, creatorRegion, creatorHubId, options) => {
  const handleCaptionError = useCallback(error => {
    Raven.captureException(error, {
      extra: {
        videoId
      }
    });
  }, [videoId]);
  const {
    data: videoCaptionData,
    loading: videoCaptionDataLoading
  } = useQuery(GET_VIDEO_CAPTION_DATA, Object.assign({
    variables: {
      videoId,
      creatorRegion,
      creatorHubId
    },
    onError: handleCaptionError
  }, options));
  const languagesWithCaptions = Object.keys(videoCaptionData || {});
  return {
    captionsAvailable: !!languagesWithCaptions.length,
    captionsLoading: videoCaptionDataLoading,
    languagesWithCaptions
  };
};